import { Component, Vue } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox, ElNotification } from 'element-plus';
import type { UploadProps, UploadUserFile } from 'element-plus';
import { Plus, Minus, Check, Delete, CirclePlusFilled, Memo, ChatLineRound, Compass, WarningFilled, DocumentCopy, Close, ArrowRightBold, ArrowLeftBold, Collection, UploadFilled } from '@element-plus/icons-vue';
import Header from '@/components/Header/Header.vue';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.vue';
import Navigation from '@/components/Navigation/Navigation.vue';
import NavigationIndex from "@/models/EnumNavigationIndex";
import AuthService from '@/services/AuthService';
import ApiService from '@/services/ApiService';

interface TransferItem
{
    key : number;
    label : string;
    disabled : boolean;
}

@Component(
{
    components:
    {
        Header,
        Breadcrumbs,
        Navigation,
        Check,
        Delete,
        Plus,
        Minus,
        Memo,
        ChatLineRound,
        Compass,
        Collection,
        WarningFilled,
        DocumentCopy,
        Close,
        ArrowRightBold,
        ArrowLeftBold,
        UploadFilled,
    },
    enums:
    {
        NavigationIndex
    }
})

export default class Question extends Vue 
{
    private activeName: string = "1";
    private loading : boolean = true;
    private loadOptions : boolean = false;
    private loadSelectedReferences : boolean = false;
    private formValid : boolean = false;
	private optionFormValid : boolean = false;
	private referenceFormValid : boolean = false;
    private hasPermission: boolean[] = [] as boolean[];
    private copyMode : boolean = false;
    private showSingleChoiceQuestion : boolean = false;
    private optionFilter : string = "";
    private insertMode : boolean = false;
    private deleteMode : boolean = false;
	private dragId : number = 0;
    private dragOverId : number = 0;
    private dragLastId : number = 0;
    private dragPositionId : number = 0;
    private deleteMode : boolean = false;
    private showOptions : boolean = false;
    private showReferences : boolean = false;
    private showCategories : boolean = false;
    private showTargetgroups : boolean = false;
	private showColorPicker : boolean = false;
    private questId : string = "0";
    private questName : string = '';
    private filter : string = '';
    private filterReference : string = '';
    private uploadURL : string = process.env.VUE_APP_REFERENCE_UPLOAD;
    private fileList : UploadUserFile[] = [] as UploadUserFile[];
    private page : number = 1;
    private rowsPerPage : number = 10;
    private pagerCount : number = 19;
    private displayList : QuestionModel[] = [] as QuestionModel[];
    private filteredList : QuestionModel[] = [] as QuestionModel[];
    private categoryModelList : CategoryModel[] = [] as CategoryModel[];
    private targetGroupModelList : TargetGroupModel[] = [] as TargetGroupModel[];
    private selectedReferencesList : OptionReferenceModel[] = [] as OptionReferenceModel[];
    private selectedReferenceText : string = "Referenzen";
    private questionModelList : QuestionModel[] = [] as QuestionModel[];
	private optionModelList : OptionModel[] = [] as OptionModel[];
	private referenceModelList : ReferenceModel[] = [] as ReferenceModel[];
	private filteredReferenceList : ReferenceModel[] = [] as ReferenceModel[];
	private sourceModelList : SourceModel[] = [] as SourceModel[];
    private copyQuestion : QuestionModel | undefined;
    private targetQuestion : QuestionModel | undefined;
    private currentQuestion : QuestionModel | undefined;
    private currentOption : OptionModel | undefined;
    private currentReference : ReferenceModel | undefined;
    private currentOptionReference : OptionReferenceModel = {} as OptionReferenceModel;
    private currentFile : UploadUserFile = {} as UploadUserFile;
    private availableCategories : TransferItem[] = [] as TransferItem[];
    private selectedCategories : number[] = [] as number[];
    private optionWarning : boolean[] = [] as boolean[];
    private availableTargetgroups : TransferItem[] = [] as TransferItem[];
    private selectedTargetgroups : number[] = [] as number[];
    private selectedCategories : number[] = [] as number[];
	private newOptionModel : OptionModel = {} as OptionModel;
    private ratingColors : string[] = ['#000000','#000000','#000000','#000000','#000000'];
    private ratingTexts : string[] = ['sehr einfach', 'einfach', 'mittel', 'schwer', 'sehr schwer'];
    private sortingField : string[] = ['Reihenfolge', 'Antwort', 'Schwierigkeit', 'Richtigkeit'];
    private sortBy : string = 'position';
    private copyReference : OptionReferenceModel | undefined;
    private windowWidth : number = window.innerWidth;
    private windowHeight : number = window.innerHeight;
    
    public async mounted()
    {
        // get permissions
        await AuthService.getPermissions();
        this.hasPermission['QSTN_SELECT'] = AuthService.hasPermission('QSTN_SELECT');
        this.hasPermission['QSTN_INSERT'] = AuthService.hasPermission('QSTN_INSERT');
        this.hasPermission['QSTN_UPDATE'] = AuthService.hasPermission('QSTN_UPDATE');
        this.hasPermission['QSTN_DELETE'] = AuthService.hasPermission('QSTN_DELETE');
        
        // load questions
        if (this.hasPermission['QSTN_SELECT'])
        {
            await this.loadCategoryList();
            await this.loadTargetgroupList();
            await this.loadQuestionList();
            await this.loadSourcesList();
        }
        
        // init filters
        if (this.questionModelList)
        {
            await this.filterList();
            this.showPage();
        }

        // set loading indicator
        window.addEventListener('resize', this.onWindowResize);
        window.addEventListener('keyup', this.onKeyUp);
        window.scrollTo(0,0);

        this.loading = false;

        if (this.$route.params.questionid)
        {
            let i : number = 0;
            let page : number = 0;
            let questionid : number = this.$route.params.questionid;
            for (let q : QuestionModel of this.questionModelList)
            {
                if (i % 10 == 0) page++;
                if (q.questionid == questionid) 
                {
                    await this.onPageChange(page);

                    if (this.$route.params.optionid)
                    {
                        await this.onOptionsEditClick(q);

                        if (this.$route.params.optionid)
                        {
                            let optionid : number = this.$route.params.optionid;
                            for (let o : OptionModel of this.optionModelList)
                            {
                                if (o.optionid == optionid)
                                {
                                    this.optionFilter = o.option;
                                    this.onOptionFilterInput(o.option);
                                }
                            }
                        }
                    }
                    else
                    {
                        await this.onTargetgroupEditClick(q);
                    }
                }
                i++;
            }
        }
    }

    private beforeUnmount()
    {
        window.removeEventListener('resize', this.onWindowResize);
        window.removeEventListener('keyup', this.onKeyUp);
    }

    private onWindowResize(e : any)
    {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
    }

    private onKeyUp(event : any)
    {
        if (event.key === 'Escape')
        {
            this.onCancelCopyOptionsClick();
        }
    }

    private async loadSourcesList()
    {
        this.loading = true;
        this.sourceModelList = await ApiService.getAllSources() as SourceModel[];
        this.loading = false;
    }

    private async filterList()
    {   
        // reset filtered list
        this.filteredList = [] as QuestionModel[];
        
        // if a filter is set, add remaining records to filtered list
        if (this.filter.length > 0)
        {
            let i:number = 0;
            for (i = 0; i < this.questionModelList.length; i++)
            {
                if (this.questionModelList[i].question.toLowerCase().includes(this.filter.toLowerCase())
                   || this.questionModelList[i].singlechoice.toLowerCase().includes(this.filter.toLowerCase())
                   || this.questionModelList[i].position.toString().toLowerCase().includes(this.filter.toLowerCase()))
                {
                    this.filteredList.push(this.questionModelList[i]);
                }
            }
            this.page = 1;
        }
        else
        {
            this.filteredList = this.questionModelList;
        }

        return this.filter;
    }

    private async showPage()
    {
        // reset display list
        this.displayList = [] as QuestionModel[];

        // only add current page records to the display list
        let i:number = 0;
        if (this.questionModelList && this.filteredList)
        {
            for (i = 0; i < this.filteredList.length; i++)
            {
                if (i >= (this.page-1)*this.rowsPerPage && i < this.page*this.rowsPerPage)
                {
                    this.displayList.push(this.filteredList![i]);
                }
            }
        }

        return this.page;
    }
	
    private async filterReferenceList()
    {   
        // reset filtered list
        this.filteredReferenceList = [] as ReferenceModel[];
        
        // if a filter is set, add remaining records to filtered list
        if (this.filterReference.length > 0)
        {
            let i:number = 0;
            for (i = 0; i < this.referenceModelList.length; i++)
            {
                if (this.referenceModelList[i].title.toLowerCase().includes(this.filterReference.toLowerCase())
                   || this.referenceModelList[i].author.toLowerCase().includes(this.filterReference.toLowerCase())
                   || this.referenceModelList[i].issue.toLowerCase().includes(this.filterReference.toLowerCase())
                   || this.referenceModelList[i].isbn.toLowerCase().includes(this.filterReference.toLowerCase()))
                {
                    this.filteredReferenceList.push(this.referenceModelList[i]);
                }
            }
        }
        else
        {
            this.filteredReferenceList = this.referenceModelList;
        }

        return this.filterReference;
    }

	private async loadCategoryList()
	{
        this.categoryModelList = await ApiService.getAllCategories() as CategoryModel[];
        
        if (this.categoryModelList)
        {
		    for (let category of this.categoryModelList)
            {
                let item : TransferItem = {} as TransferItem;
                item.key = category.categoryid;
                item.label = category.name;

                this.availableCategories.push(item);
            }
        }
	}
    
    private async loadSelectedCategories()
    {
        // abort, if no question has been selected
        if (!this.currentQuestion) return;

        // reset the current selection
        this.selectedCategories = [] as number[];

        // get list of selected categories for this question 
        let list : QuestionCategoryModel[] | undefined = await ApiService.getQuestionCategories(this.currentQuestion.questionid.toString());
       
        // abort, if none are assigned yet
        if (!list) return;
        
        // push all assigned items to selection
		for (let item : QuestionCategoryModel of list)
        {
            this.selectedCategories.push(item.categoryid);
        }
    }

	private async loadTargetgroupList()
	{
        this.targetGroupModelList = await ApiService.getAllTargetGroups() as TargetGroupModel[];

		for (let tg of this.targetGroupModelList)
        {
            let item : TransferItem = {} as TransferItem;
            item.key = tg.targetgroupid;
            item.label = tg.name;

            this.availableTargetgroups.push(item);
        }
	}
    
    private async loadSelectedTargetgroups()
    {
        // abort, if no question has been selected
        if (!this.currentQuestion) return;

        // reset the current selection
        this.selectedTargetgroups = [] as number[];

        // get list of selected targetgroups for this question 
        let list : QuestionTargetGroupModel[] | undefined = await ApiService.getQuestionTargetGroups(this.currentQuestion.questionid.toString());
       
        // abort, if none are assigned yet
        if (!list) return;
        
        // push all assigned items to selection
		for (let item : QuestionTargetGroupModel of list)
        {
            this.selectedTargetgroups.push(item.targetgroupid);
        }
    }

    private async loadQuestionList()
    {
        this.questionModelList = await ApiService.getAllQuestions();
    }

	private async loadQuestionOptions()
    {
        // abort, if no question is currently selected
		if (!this.currentQuestion) return;

		// reset current list
		this.optionModelList = [] as OptionModel[];

        // request options for given question from server
        let list = await ApiService.getAllOptionsForQuestion(this.currentQuestion.questionid.toString(), this.optionFilter, this.sortBy);

        if (list)
        {
            this.optionModelList = list;
        }
    }

	private async loadAvailableReferences()
    {
		if (!this.currentQuestion) return;

		// reset current list
		this.referenceModelList = [] as ReferenceModel[];

        // request references for given option from server
        let list = await ApiService.getAllAvailableReferences(this.currentOption.optionid.toString());

        if (list)
        {
            this.referenceModelList = list;
        }
    }

	private async loadSelectedReferences()
    {
        this.loadingSelectedReferences= true;

		if (!this.currentQuestion || !this.currentOption) return;

		// reset current list
		this.selectedReferencesList = [] as ReferenceModel[];

        // request references for given option from server
        let list = await ApiService.getAllReferencesForOption(this.currentOption.optionid.toString());

        if (list)
        {
            this.selectedReferencesList = list;
        }

        if (this.currentOptionReference && this.currentOptionReference.referenceid > 0)
        {
            this.currentOptionReference = undefined;
        }
        
        this.loadingSelectedReferences = false;
    }
        
	private async loadOptionReferenceFiles()
    {
		if (!this.currentQuestion || !this.currentOption || !this.currentReference) return;

        // request files for given reference from server
        let list : FileModel[] = await ApiService.getAllFilesForOptionReference(this.currentOption.optionid.toString(), this.currentReference.referenceid.toString());
        
        this.fileList = [] as UploadUserFile[];
        
        if (list)
        {
            for (let f : FileModel of list)
            {
                let file = {} as UploadUserFile;
                file.uid = f.fileid;
                file.name = f.filepath;
                this.fileList.push(file);
            }
        }
    }
        
    private async loadOptionReference()
    {
        if (!this.currentOption || !this.currentReference) return;

        this.currentOptionReference = await ApiService.getOptionReference(this.currentOption.optionid.toString(), this.currentReference.referenceid.toString());
    }

    private async deleteQuestion(question : QuestionModel)
    {
        let ok = await ApiService.deleteQuestion(this.currentQuestion);

        if (ok)
        {
            ElNotification(
            {
                title: 'OK',
                message: 'Frage wurde gelöscht.',
                type: 'success',
                offset: 36
            });

            await this.loadQuestionList();
            await this.filterList();
            if (this.page > Math.ceil(this.filteredList!.length/this.rowsPerPage))
            {
                this.page = Math.ceil(this.filteredList!.length/this.rowsPerPage);
            }
            await this.showPage();
        }
        else
        {
            // or show error message
			ElMessage({message: "Frage konnte nicht gelöscht werden.",	type: 'error'});
        }
    }

    private async createQuestion()
    {
        let position : number = 1;
        let sourceQuestion : QuestionModel | undefined;

        // get last question row
        if (this.filteredList && this.filteredList!.length > 0)
        {
            position = this.filteredList[this.filteredList.length-1].position+1;
            sourceQuestion = this.filteredList[this.filteredList.length-1];
        }

        let newQuestion : QuestionModel | undefined = {} as QuestionModel;

        newQuestion.position = position;
        newQuestion.sourceid = 1;
        newQuestion.question = "Frage " + position;

        newQuestion = await ApiService.createQuestion(newQuestion);

        if (newQuestion)
        {
            await this.loadQuestionList();
            await this.filterList();
            this.page = Math.ceil((this.filteredList.length)/this.rowsPerPage);
            await this.showPage();


            ElNotification(
            {
                title: 'OK',
                message: 'Neue Frage wurde erstellt.',
                type: 'success',
                offset: 36
            });

            this.insertMode = false;
        }
        else
        {
			ElMessage({message: "Frage konnte nicht erstellt werden.",	type: 'error'});
        }
    }

    private async saveQuestion(question : QuestionModel, notify : boolean = true)
    {
        let ok : any = await ApiService.updateQuestion(question);
        
        if (!notify) return;

        if (ok)
        {
            ElNotification(
            {
                title: 'OK',
                message: 'Änderungen wurden übernommen.',
                type: 'success',
                offset: 36
            });
        }
        else
        {
            ElNotification(
            {
                title: 'OK',
                message: 'Änderungen konnten nicht gespeichert werden.',
                type: 'error',
                offset: 36
            });
        }
    }
	
	private async storeCategorySelection()
    {
        if (this.currentQuestion)
        {
            // remove all available first
            for (let del of this.availableCategories)
            {
                await ApiService.removeQuestionCategory(this.currentQuestion.questionid.toString(), del.key.toString());
            }

            // insert selected categories
            for (let ins of this.selectedCategories)
            {
                let item : QuestionCategoryModel = {} as QuestionCategoryModel;
                item.questionid = this.currentQuestion.questionid;
                item.categoryid = ins;
                await ApiService.addQuestionCategory(item);
            }
        }
    }

	private async storeTargetgroupSelection()
    {
        if (this.currentQuestion)
        {
            // remove all available first
            for (let del of this.availableTargetgroups)
            {
                await ApiService.removeQuestionTargetGroup(this.currentQuestion.questionid.toString(), del.key.toString());
            }

            // insert selected targetgroups
            for (let ins of this.selectedTargetgroups)
            {
                let item : QuestionTargetGroupModel = {} as QuestionTargetGroupModel;
                item.questionid = this.currentQuestion.questionid;
                item.targetgroupid = ins;
                await ApiService.addQuestionTargetGroup(item);
            }
        }
    }

    private async storeOptionReference()
    {
        this.currentOptionReference = await ApiService.updateOptionReference(this.currentOptionReference);
        
        if (this.currentOptionReference)
        {
            /*
            ElNotification(
            {
                title: 'OK',
                message: 'Die Infos zur Referenz wurden gespeichert.',
                type: 'success',
                offset: 36
            });
            */
        }
        else
        {
            ElNotification(
            {
                title: 'FEHLER',
                message: 'Die Infos zur Referenz konnten nicht gespeichert werden.',
                type: 'error',
                offset: 36
            });
        }
    }

    private async wait(ms : number)
    {
        return new Promise(resolve => 
        {
            setTimeout(resolve, ms);
        });
    }

    private onNewOptionEnter()
    {
        console.log("Enter");
    }

	private onOptionInput(key :any)
    {
        this.optionFormValid = (this.newOptionModel && this.newOptionModel.option.length > 0);
    }

    private onNewOptionClick()
    {
        // create new option by sending request to server
        if (this.optionFormValid)
        {
            this.createOption();
        }
    }

    private onQuestionDeleteClick(question : QuestionModel)
    {
        this.currentQuestion = question;

        ElMessageBox.confirm('Soll diese Frage wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onDeleteQuestionConfirm,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });
    }

    private onQuestionSaveClick(question : QuestionModel)
    {
        this.saveQuestion(question);
    }

    private onNewQuestionClick()
    {
        this.insertMode = true;

        // save potential changes on last row
        if (this.displayList && this.displayList.length > 0)
        {
            this.saveQuestion(this.displayList[this.displayList.length-1], false);
        }
        
        // reset filter
        this.filter = '';

        // create new question
        this.createQuestion();
    }

    private onPageChange(p : number)
    {
        this.page = p;
        this.showPage();
    }

    private onFilterChange()
    {
        this.filterList();
        this.showPage();
    }

    private onReferenceFilterChange()
    {
        this.filterReferenceList();
    }

    private onDeleteQuestionConfirm(action : string)
    {
        if (this.currentQuestion && action == 'confirm')
        {
            this.deleteQuestion(this.currentQuestion);
        }
    }

    private async onCategoryEditClick(question : QuestionModel)
    {
        this.currentQuestion = question;
        await this.loadSelectedCategories();
        this.showCategories = true;
    }

    private async onTargetgroupEditClick(question : QuestionModel)
    {
        this.currentQuestion = question;
        await this.loadSelectedTargetgroups();
        this.showTargetgroups = true;
    }

    private onCategoryChange()
    {
        this.storeCategorySelection();
    }

    private onTargetgroupChange()
    {
        this.storeTargetgroupSelection();
    }

    private onOptionDialogOpened()
    {
        this.$refs.optionsList.scrollTop = 0;
    }

    private async onOptionsEditClick(question : QuestionModel)
    {
        this.currentQuestion = question;
        this.questName = question.question;
        this.optionFilter = '';

		await this.loadQuestionOptions();
        this.showOptions = true;
    }

    private async onOptionSaveClick(option : OptionModel)
    {
        if (!option) return;

        option = await ApiService.updateOption(option);

        if (option)
        {
            /*
            ElNotification(
            {
                title: 'OK',
                message: 'Die Antwortmöglichkeit wurde gespeichert.',
                type: 'success',
                offset: 36
            });
            */
        }
        else
        {
            ElNotification(
            {
                title: 'FEHLER',
                message: 'Die Antwortmöglichkeit konnte nicht gespeichert werden.',
                type: 'error',
                offset: 36
            });
        }
    }

	private async createOption()
    {
		if (this.currentQuestion)
		{
			this.newOptionModel.questionid = this.currentQuestion.questionid;
			this.newOptionModel.position = 1;

			if (this.optionModelList.length > 0)
			{
				this.newOptionModel.position = await ApiService.getMaxOptionPosition(this.currentQuestion.questionid) + 1;
			}

			this.newOptionModel = await ApiService.createOption(this.newOptionModel);

			if (this.newOptionModel && this.newOptionModel.optionid > 0)
			{
                /*
				ElNotification(
				{
					title: 'OK',
					message: 'Neue Option wurde hinzugefügt.',
					type: 'success',
					offset: 36
				});
                */

				await this.loadQuestionOptions();
				this.newOptionModel = {} as OptionModel;
				this.optionFormValid = false;
			}
			else
			{
				// or show error message
				ElNotification(
				{
					title: 'Fehler',
					message: 'Neue Option konnte nicht hinzugefügt werden.',
					type: 'error',
					offset: 36
				});
			}
		}
    }

    private async deleteOption(option : OptionModel)
    {
        let ok = await ApiService.deleteOption(option);

        if (ok)
        {
            ElNotification(
            {
                title: 'OK',
                message: 'Option wurde gelöscht.',
                type: 'success',
                offset: 36
            });

            await this.loadQuestionOptions();
        }
        else
        {
            // or show error message
			ElMessage({message: "Ausprägung konnte nicht gelöscht werden.", type: 'error'});
        }
    }

	private async switchOptions(optionSource : OptionModel, optionTarget : OptionModel)
    {
        this.loadOptions = true;

        // get both positions
        let sourcePosition = optionSource.position;
        let targetPosition = optionTarget.position;

        // assign switched positions
        optionSource.position = targetPosition;
        optionTarget.position = sourcePosition;

        // send update request to server
        await ApiService.updateOption(optionSource);
        await ApiService.updateOption(optionTarget);

        // reload options list
        await this.loadQuestionOptions();

        this.dragId = 0;
        this.dragOverId = 0;
        this.dragPositionId = 0;

        this.loadOptions = false;
    }

    private async shiftOption(option : OptionModel)
    {
        this.loadOptions = true;
		
        await ApiService.reorderOption(option);
        await this.loadQuestionOptions();

        this.dragId = 0;
        this.dragOverId = 0;
        this.dragLastId = 0;
        this.dragPositionId = 0;

        this.loadOptions = false;
    } 

	private async addReference(record : OptionReferenceModel)
    {
        record = await ApiService.createOptionReference(record);

        if (record.dateinsert)
        {
            /*
            ElNotification(
            {
                title: 'OK',
                message: 'Referenz wurde hinzugefügt.',
                type: 'success',
                offset: 36
            });
            */
            await this.loadAvailableReferences();
            await this.loadSelectedReferences();
        }
        else
        {
            // or show error message
            ElNotification(
            {
                title: 'Fehler',
                message: 'Referenz konnte nicht hinzugefügt werden.',
                type: 'error',
                offset: 36
            });
        }
    }

    private async removeReference(optionid : string, referenceid : string)
    {
        let ok = await ApiService.deleteOptionReference(optionid, referenceid);

        if (ok)
        {
            /*
            ElNotification(
            {
                title: 'OK',
                message: 'Referenz wurde entfernt.',
                type: 'success',
                offset: 36
            });
            */

            await this.loadQuestionOptions();
            await this.loadAvailableReferences();
            await this.loadSelectedReferences();
        }
        else
        {
            // or show error message
			ElMessage({message: "Referenz konnte nicht entfernt werden.", type: 'error'});
        }
    }

    private async appendOptions()
    {
        // abort, if source or target is not set
        if (!this.copyQuestion || !this.targetQuestion) return;

        let rowsAdded : number = 0;

        rowsAdded = await ApiService.appendOptions(this.copyQuestion.questionid.toString(), this.targetQuestion.questionid.toString());

        if (rowsAdded > 0)
        {
            ElNotification(
            {
                title: 'OK',
                message: `Es wurden ${rowsAdded} Antwortmöglichkeiten erfolgreich in die Zielfrage kopiert.`,
                type: 'success',
                offset: 36
            });
        }
        else
        {
            ElNotification(
            {
                title: 'Fehler',
                message: 'Beim Hinzufügen der Antwortmöglichkeiten ist ein Fehler aufgetreten.',
                type: 'error',
                offset: 36
            });
        }
    }

	private onOptionDelete()
    {
        for (const option of this.optionModelList) 
        {
            if (option.optionid == this.dragId)
            {
                this.deleteOption(option);
                break;
            }
        }

        this.deleteMode = false;
    }
    
	private async onPositionConfirm(ev : any)
    {
		if (this.currentOption && ev.action == "confirm" && ev.value && !isNaN(ev.value.replace(',', '.')))
		{
			let val : number = Math.round(ev.value.replace(',','.')) as number;
            this.currentOption.position = val;
			await ApiService.updateOption(this.currentOption);
		}
    }

	private async onOptionCorrectChange(option : OptionModel)
	{
		this.currentOption = option;

        let ok: boolean = await ApiService.updateOption(option);
        
        if (!ok)
        {
			ElMessage({message: "Option konnte nicht geändert werden.", type: 'error'});
        }
	}

	private async onOptionDifficultyChange(option : OptionModel)
	{
		this.currentOption = option;

        let ok: boolean = await ApiService.updateOption(option);
        
        if (!ok)
        {
			ElMessage({message: "Option konnte nicht geändert werden.", type: 'error'});
        }
	}

	private onOptionPositionClick(option : OptionModel)
	{
		this.currentOption = option;
        
		ElMessageBox.prompt('Reihenfolge:', 
        {
            callback: this.onPositionConfirm,
            confirmButtonText: 'Speichern',
            cancelButtonText: 'Abbrechen',
			inputValue: option.position
        });
	}
    
    private async onOptionFilterInput()
    {
        await this.loadQuestionOptions();
    }

	private async onOptionReferenceClick(option : OptionModel)
	{
		this.currentOption = option;
        this.selectedReferenceText = option.option;
		await this.loadAvailableReferences();
		await this.loadSelectedReferences();
        await this.filterReferenceList();
        this.showReferences = true;
	}

	private async onReferenceClick(reference : ReferenceModel)
	{
        window.open(reference.reference, '_blank');
	}
    
    private onCopyOptionsClick(question : QuestionModel)
    {
        if (!question) return;

        this.copyQuestion = question;
        this.copyMode = true;
    }

    private onCancelCopyOptionsClick()
    {
        this.copyQuestion = undefined;
        this.copyMode = false;
    }

    private onConfirmCopyOptionsClick(target : QuestionModel)
    {
        if (!this.copyQuestion || !target) return;

        this.targetQuestion = target;

        this.appendOptions();

        this.copyMode = false;
    }

    private onSortingChange()
    {
        this.loadQuestionOptions();
    }

    private onAddReferenceClick(reference : ReferenceModel)
    {
        if (!this.currentOption) return;
        
        let model : OptionReferenceModel = {} as OptionReferenceModel;

        model.optionid = this.currentOption.optionid;
        model.referenceid = reference.referenceid;

        this.addReference(model);
    }

    private onRemoveReferenceClick(reference : ReferenceModel)
    {
        if (!this.currentOption) return;

        this.removeReference(this.currentOption.optionid.toString(), reference.referenceid.toString());
    }

    private onReferenceSelect(reference : ReferenceModel)
    {
        if (!reference) return;
        
        this.currentReference = reference;

        this.uploadURL = `${process.env.VUE_APP_REST_API_PATH}/file/option/${this.currentOption.optionid.toString()}/reference/${this.currentReference.referenceid.toString()}`

        this.loadOptionReference();
        this.loadOptionReferenceFiles();
    }

    private onReferenceSaveClick()
    {
        if (!this.currentOptionReference) return;

        this.storeOptionReference(); 
    }

    private onShowSingleChoiceClick()
    {
        this.showSingleChoiceQuestion = !this.showSingleChoiceQuestion;
    }

    private async onFilePreview(file : UploadUserFile)
    {
        let fileModel : FileModel | undefined = await ApiService.getFile(file.uid.toString());

        if (fileModel)
        {
		    let data : Blob | undefined = await ApiService.downloadFile(fileModel.fileid.toString());

            if (data)
            {
                let blob : Blob = new Blob([data], {type: fileModel.mimetype});
                let fileURL : string = window.URL.createObjectURL(blob);
                let fileLink : HTMLAnchorElement = document.createElement('a');

                fileLink.href = fileURL;
                fileLink.download = fileModel.filepath;
                document.body.appendChild(fileLink);

                fileLink.click();

                URL.revokeObjectURL(fileLink.href);
            }
        }
    }

    private async onConfirmFileRemove(action : any)
    {
        if (action == "confirm")
        {
            await ApiService.deleteFile(this.currentFile.uid.toString());
            await this.loadOptionReferenceFiles();
        }
    }

    private onFileBeforeRemove(file : UploadUserFile)
    {
        this.currentFile = file;

        ElMessageBox.confirm('Soll dieser Anhang wirklich gelöscht werden?', 'Warning', 
        {
            type: 'warning',
            title: '',
            callback: this.onConfirmFileRemove,
            confirmButtonText: 'Löschen',
            cancelButtonText: 'Abbrechen'
        });

        return false;
    }

    private async onFileSuccess(file : UploadUserFile)
    {
        await this.loadOptionReferenceFiles();
    }

    private onFileRemove()
    {
        console.log("removed file")
    }

    public onFileExeed()
    {
    }

    private onReferenceCopyClick()
    {
        this.copyReference = this.currentOptionReference;
    }

    private async onReferencePasteClick()
    {
        if (this.copyReference != null && this.currentOption != null)
        {
            let model : OptionReferenceModel = {} as OptionReferenceModel;

            model.referenceid = this.copyReference.referenceid;
            model.optionid = this.currentOption.optionid;
            model.info = this.copyReference.info;
            model.url = this.copyReference.url;

            await ApiService.createOptionReference(model);

            let fileList : FileModel[] | undefined = await ApiService.getAllFilesForOptionReference(this.copyReference.optionid.toString(), this.copyReference.referenceid.toString());
            
            if (fileList)
            {
                for (let file of fileList)
                {
                    let fileModel : OptionReferenceFileModel = {} as OptionReferenceFileModel;
                    fileModel.fileid = file.fileid;
                    fileModel.optionid = this.currentOption.optionid;
                    fileModel.referenceid = this.copyReference.referenceid;

                    await ApiService.createOptionReferenceFile(fileModel);
                }
            }

            await this.loadOptionReference();
            await this.loadOptionReferenceFiles();
            await this.loadAvailableReferences();
            await this.loadSelectedReferences();
        }
    }

    private isAssigned()
    {
        if (this.copyReference)
        {
            for (let ref of this.selectedReferencesList)
            {
                if (ref.referenceid == this.copyReference.referenceid) return true;
            }
        }

        return false;
    }
}
